import { useEffect, useRef, useState } from 'react';
import {
  PHONE_COUNTRIES,
  DEFAULT_PHONE_DIAL,
  parsePhoneValue,
  combinePhone,
  digitsOnly,
} from '../../utils/phone';

/**
 * Campo de teléfono con selector de indicativo de país.
 * El indicativo vive solo en el <select>; el input es 100% editable (número nacional).
 * Emite dígitos internacionales (ej. 573001234567).
 */
export default function PhoneField({
  label,
  value = '',
  onChange,
  required = false,
  disabled = false,
  id,
  name,
  className = '',
  labelClassName = 'label',
  inputClassName = 'input',
  selectClassName,
  defaultCountryDial = DEFAULT_PHONE_DIAL,
  placeholder = '300 123 4567',
  hint,
}) {
  const initial = parsePhoneValue(value, defaultCountryDial);
  const [dialCode, setDialCode] = useState(initial.dialCode);
  const [national, setNational] = useState(initial.national);
  const lastEmittedRef = useRef(value);

  useEffect(() => {
    if (digitsOnly(value) === digitsOnly(lastEmittedRef.current)) return;
    lastEmittedRef.current = value;
    const parsed = parsePhoneValue(value, defaultCountryDial);
    setDialCode(parsed.dialCode);
    setNational(parsed.national);
  }, [value, defaultCountryDial]);

  const emit = (nextDial, nextNational) => {
    const combined = combinePhone(nextDial, nextNational);
    lastEmittedRef.current = combined;
    onChange?.(combined);
  };

  const normalizeNational = (raw, dial) => {
    const digits = digitsOnly(raw);
    if (!digits) return '';
    const dialDigits = digitsOnly(dial);
    if (dialDigits && digits.startsWith(dialDigits) && digits.length > dialDigits.length + 3) {
      return digits.slice(dialDigits.length);
    }
    return digits;
  };

  const dialCls = selectClassName || `phone-field-dial ${inputClassName || ''}`.trim();
  const numberCls = `phone-field-number ${inputClassName || 'input'}`.trim();

  return (
    <div className={`phone-field ${className}`.trim()}>
      {label ? (
        <label htmlFor={id} className={`${labelClassName} block mb-1`}>
          {label}
          {required ? <span className="text-red-600"> *</span> : null}
        </label>
      ) : null}
      <div className="flex gap-2 items-stretch">
        <select
          className={dialCls}
          value={dialCode}
          disabled={disabled}
          style={{ width: '8.5rem', flexShrink: 0 }}
          onChange={(e) => {
            const next = e.target.value;
            setDialCode(next);
            emit(next, national);
          }}
          aria-label="Indicativo de país"
        >
          {PHONE_COUNTRIES.map((c) => (
            <option key={`${c.code}-${c.dial}`} value={c.dial}>
              {c.flag} +{c.dial} {c.name}
            </option>
          ))}
        </select>
        <input
          type="tel"
          id={id}
          name={name}
          className={numberCls}
          style={{ flex: '1 1 0%', minWidth: 160, width: 'auto' }}
          value={national}
          disabled={disabled}
          required={required}
          placeholder={placeholder}
          onChange={(e) => {
            const next = normalizeNational(e.target.value, dialCode);
            setNational(next);
            emit(dialCode, next);
          }}
          inputMode="numeric"
          autoComplete="tel-national"
        />
      </div>
      {hint ? <p className="text-xs text-stone-500 mt-1">{hint}</p> : null}
    </div>
  );
}
